/**
 * Editing-context extraction for semantic snapshots.
 *
 * This layer summarizes form-like editing work that is otherwise hard to
 * infer from a single accessibility tree, especially inline grid editors
 * whose fields are gridcells until focused.
 *
 * @module
 */

import { parseSnapshot, type SnapshotElement } from './snapshot-analyzer.js';
import { detectGrid } from './snapshot-formatter-grid.js';
import {
  FORM_INPUT_ROLES,
  getDisplayText,
  isActiveElement,
  isBlankValue,
  isDisabledElement,
  isReadonlyElement,
  stripBlankPatterns,
  subtreeContainsActive,
  type GridCell,
  type GridInfo,
  type GridRow,
} from './snapshot-formatter-shared.js';

export type EditingContextKind = 'native-form' | 'inline-grid-edit';
export type EditingContextConfidence = 'high' | 'medium' | 'low';
export type EditingFieldState = 'blank' | 'filled' | 'active' | 'invalid' | 'readonly';
export type EditingRequiredness = 'confirmed' | 'candidate' | 'unknown';
export type EditingInteraction =
  | 'type-input'
  | 'choose-option'
  | 'toggle'
  | 'click-cell'
  | 'readonly';

export interface EditingField {
  identity: string;
  label: string;
  ref: string;
  role?: string;
  value: string;
  states: EditingFieldState[];
  requiredness: EditingRequiredness;
  interaction: EditingInteraction;
}

export interface EditingSubmitCandidate {
  ref: string;
  label: string;
  disabled: boolean;
  inference: 'native-submit' | 'label-match' | 'toolbar-label';
  blockers: string[];
}

export interface EditingContext {
  id: string;
  kind: EditingContextKind;
  flavor: string;
  confidence: EditingContextConfidence;
  surfaceRef: string;
  surfaceLabel: string;
  recordRef?: string;
  recordLabel?: string;
  recordIndex?: number;
  fields: EditingField[];
  submitCandidates: EditingSubmitCandidate[];
  blockers: string[];
}

export type EditingContextChangeKind =
  | 'context-appeared'
  | 'context-removed'
  | 'field-active'
  | 'field-filled'
  | 'field-cleared'
  | 'field-changed'
  | 'submit-enabled'
  | 'submit-disabled'
  | 'blocker-added'
  | 'blocker-removed'
  | 'save-blocked'
  | 'save-succeeded';

export interface EditingContextChange {
  kind: EditingContextChangeKind;
  message: string;
  contextId?: string;
  surfaceLabel?: string;
  recordLabel?: string;
  fieldIdentity?: string;
  fieldLabel?: string;
  ref?: string;
  before?: string | boolean;
  after?: string | boolean;
}

const FIELD_ROLES = new Set([...FORM_INPUT_ROLES, 'checkbox', 'radio', 'switch']);

const GRID_ROLES = new Set(['grid', 'treegrid', 'table']);

// These labels are intentionally English-only: they are hints for the
// agent, not source-of-truth validation semantics.
const SUBMIT_LABEL_RE = /\b(save(?:\s+changes)?|submit|apply|create|update|done)\b/i;
const BLOCKER_TEXT_RE =
  /\b(required|invalid|validation|error|missing|must|cannot|can't|please\s+(?:enter|select|choose|fill))\b/i;
const SPECIFIC_BLOCKER_TEXT_RE =
  /\b(?:is|are)\s+(?:required|invalid|missing)\b|\b(?:must|cannot|can't)\b|\bplease\s+(?:enter|select|choose|fill)\b/i;
const GENERIC_REQUIRED_COPY_RE =
  /\b(?:all fields|fields marked|required fields|all required fields)\b/i;
const DRAFT_ROW_RE =
  /\b(new|draft|unsaved|editing|edited|pending|validation|invalid|required|error)\b/i;
const READONLY_LABEL_RE =
  /\b(created|updated|last\s+updated|modified|id|uuid|actions?|options?|menu)\b/i;
const SKIP_FIELD_LABEL_RE =
  /^(?:#|select|select row|row selector|actions?|options?|menu|open menu|☐)$/i;
const PLACEHOLDER_RE = /^(?:choose|select|pick|enter|search)\b/i;
const BLANK_CELL_RE = /^blank\b.*\b(?:cell|field|input)\b/i;
const LIVE_FEEDBACK_ROLES = new Set(['alert', 'status', 'log']);
const INLINE_GRID_SCOPE_ROLES = new Set([
  'alertdialog',
  'dialog',
  'form',
  'group',
  'region',
  'section',
  'tabpanel',
]);
const INLINE_GRID_NEARBY_SIBLING_WINDOW = 3;

interface FlatElement {
  element: SnapshotElement;
  parent: SnapshotElement | null;
  depth: number;
  index: number;
}

interface ExtractionEnvironment {
  roots: SnapshotElement[];
  flat: FlatElement[];
  byRef: Map<string, SnapshotElement>;
  parentByElement: Map<SnapshotElement, SnapshotElement | null>;
}

interface MatchedEditingContext {
  before: EditingContext;
  after: EditingContext;
}

/**
 * Extract editing contexts from raw snapshot YAML.
 */
export function extractEditingContexts(yaml: string): EditingContext[] {
  if (!yaml.trim()) return [];
  return extractEditingContextsFromElements(parseSnapshot(yaml));
}

/**
 * Extract editing contexts from parsed snapshot elements.
 */
export function extractEditingContextsFromElements(elements: SnapshotElement[]): EditingContext[] {
  const env = buildEnvironment(elements);
  const contexts: EditingContext[] = [];

  contexts.push(...extractNativeFormContexts(env));
  contexts.push(...extractInlineGridContexts(env));

  return dedupeContexts(contexts).sort(compareContextPriority);
}

/**
 * Compare two extracted context sets using stable field identities.
 */
export function compareEditingContexts(
  beforeContexts: EditingContext[],
  afterContexts: EditingContext[]
): EditingContextChange[] {
  const before = filterVisibleContexts(beforeContexts);
  const after = filterVisibleContexts(afterContexts);
  const changes: EditingContextChange[] = [];

  const { matches, unmatchedBefore, unmatchedAfter } = matchEditingContexts(before, after);

  for (const context of unmatchedAfter) {
    changes.push({
      kind: 'context-appeared',
      contextId: context.id,
      surfaceLabel: context.surfaceLabel,
      recordLabel: context.recordLabel,
      message: `${describeContext(context)} appeared${context.recordLabel ? ` for "${context.recordLabel}"` : ''}.`,
    });
  }

  for (const context of unmatchedBefore) {
    const wasBlocked =
      context.submitCandidates.some((submit) => submit.disabled) || context.blockers.length > 0;
    changes.push({
      kind: wasBlocked ? 'context-removed' : 'save-succeeded',
      contextId: context.id,
      surfaceLabel: context.surfaceLabel,
      recordLabel: context.recordLabel,
      message: wasBlocked
        ? `${describeContext(context)} disappeared; edit mode likely closed or re-rendered.`
        : `${describeContext(context)} disappeared after save/edit completion.`,
    });
  }

  compareMatchedFields(matches, changes);
  compareSubmitState(matches, changes);
  compareBlockers(matches, changes);

  return dedupeChanges(changes);
}

function compareMatchedFields(
  matches: MatchedEditingContext[],
  changes: EditingContextChange[]
): void {
  for (const match of matches) {
    const beforeFields = fieldsByCompareKey(match.before);
    const afterFields = fieldsByCompareKey(match.after);

    for (const [identity, afterField] of afterFields) {
      const beforeField = beforeFields.get(identity);
      if (!beforeField) continue;

      const wasActive = hasFieldState(beforeField, 'active');
      const isActive = hasFieldState(afterField, 'active');
      if (!wasActive && isActive) {
        changes.push({
          kind: 'field-active',
          contextId: match.after.id,
          surfaceLabel: match.after.surfaceLabel,
          recordLabel: match.after.recordLabel,
          fieldIdentity: afterField.identity,
          fieldLabel: afterField.label,
          ref: afterField.ref,
          message: `Field "${afterField.label}" became active in ${match.after.surfaceLabel}.`,
        });
      }

      const beforeBlank = hasFieldState(beforeField, 'blank');
      const afterBlank = hasFieldState(afterField, 'blank');
      if (beforeBlank && !afterBlank) {
        changes.push({
          kind: 'field-filled',
          contextId: match.after.id,
          surfaceLabel: match.after.surfaceLabel,
          recordLabel: match.after.recordLabel,
          fieldIdentity: afterField.identity,
          fieldLabel: afterField.label,
          ref: afterField.ref,
          before: beforeField.value,
          after: afterField.value,
          message: `Field "${afterField.label}" changed from blank to "${afterField.value}".`,
        });
        continue;
      }

      if (!beforeBlank && afterBlank) {
        changes.push({
          kind: 'field-cleared',
          contextId: match.after.id,
          surfaceLabel: match.after.surfaceLabel,
          recordLabel: match.after.recordLabel,
          fieldIdentity: afterField.identity,
          fieldLabel: afterField.label,
          ref: afterField.ref,
          before: beforeField.value,
          after: afterField.value,
          message: `Field "${afterField.label}" was cleared.`,
        });
        continue;
      }

      if (!afterBlank && beforeField.value !== afterField.value) {
        changes.push({
          kind: 'field-changed',
          contextId: match.after.id,
          surfaceLabel: match.after.surfaceLabel,
          recordLabel: match.after.recordLabel,
          fieldIdentity: afterField.identity,
          fieldLabel: afterField.label,
          ref: afterField.ref,
          before: beforeField.value,
          after: afterField.value,
          message: `Field "${afterField.label}" changed from "${beforeField.value}" to "${afterField.value}".`,
        });
      }
    }
  }
}

/**
 * Render high-signal editing contexts as a compact block.
 */
export function formatEditingContexts(contexts: EditingContext[]): string {
  const visible = filterVisibleContexts(contexts).slice(0, 2);
  if (visible.length === 0) return '';

  const lines = ['EDITING CONTEXTS:'];
  for (const context of visible) {
    const record = context.recordLabel
      ? `, record "${context.recordLabel}" [${context.recordRef ?? 'no-ref'}]`
      : '';
    lines.push(
      `  - ${describeContext(context)} (${context.confidence}, ${context.flavor}) on "${context.surfaceLabel}" [${context.surfaceRef}]${record}`
    );

    const fields = context.fields.slice(0, 8);
    if (fields.length > 0) {
      lines.push('    Fields:');
      for (const field of fields) {
        const stateText = field.states.join(', ');
        const requiredText =
          field.requiredness === 'unknown' ? '' : `, ${field.requiredness} required`;
        const valueText = field.value ? `"${field.value}"` : 'blank';
        lines.push(
          `      - ${field.label} [${field.ref}]: ${valueText} (${stateText}${requiredText}; ${formatInteraction(field.interaction)})`
        );
      }
      if (context.fields.length > fields.length) {
        lines.push(`      ... +${context.fields.length - fields.length} more fields`);
      }
    }

    const submitCandidates = context.submitCandidates.slice(0, 3);
    if (submitCandidates.length > 0) {
      lines.push('    Submit:');
      for (const submit of submitCandidates) {
        const disabledText = submit.disabled ? 'disabled' : 'enabled';
        const inferenceText =
          submit.inference === 'native-submit' ? 'native submit' : 'inferred submit';
        lines.push(`      - ${submit.label} [${submit.ref}]: ${disabledText}, ${inferenceText}`);
      }
    }

    const blockers = context.blockers.slice(0, 3);
    if (blockers.length > 0) {
      lines.push('    Blockers:');
      for (const blocker of blockers) {
        lines.push(`      - ${blocker}`);
      }
    }
  }

  return lines.join('\n');
}

function buildEnvironment(elements: SnapshotElement[]): ExtractionEnvironment {
  const flat: FlatElement[] = [];
  const byRef = new Map<string, SnapshotElement>();
  const parentByElement = new Map<SnapshotElement, SnapshotElement | null>();

  const walk = (
    element: SnapshotElement,
    parent: SnapshotElement | null,
    depth: number,
    index: number
  ): void => {
    flat.push({ element, parent, depth, index });
    parentByElement.set(element, parent);
    if (element.ref) byRef.set(element.ref, element);
    element.children.forEach((child, childIndex) => walk(child, element, depth + 1, childIndex));
  };

  elements.forEach((element, index) => walk(element, null, 0, index));

  return {
    roots: elements,
    flat,
    byRef,
    parentByElement,
  };
}

function extractNativeFormContexts(env: ExtractionEnvironment): EditingContext[] {
  const contexts: EditingContext[] = [];

  for (const entry of env.flat) {
    const form = entry.element;
    if (form.role !== 'form') continue;

    const blockers = collectBlockers([form]);
    const submitCandidates = collectSubmitCandidates([form], blockers, 'native-submit');
    const fields = collectFormFields(
      form,
      blockers,
      submitCandidates.some((submit) => submit.disabled)
    );
    if (fields.length === 0) continue;

    const surfaceLabel = elementLabel(form) || 'form';
    const id = buildContextIdentity('native-form', surfaceLabel, undefined, undefined);
    const confidence: EditingContextConfidence =
      submitCandidates.length > 0 || fields.some((field) => field.requiredness === 'confirmed')
        ? 'high'
        : 'medium';

    contexts.push({
      id,
      kind: 'native-form',
      flavor: 'native-form',
      confidence,
      surfaceRef: form.ref,
      surfaceLabel,
      fields: fields.map((field) => ({
        ...field,
        identity: buildFieldIdentity(
          'native-form',
          surfaceLabel,
          undefined,
          undefined,
          field.label
        ),
      })),
      submitCandidates,
      blockers,
    });
  }

  return contexts;
}

function extractInlineGridContexts(env: ExtractionEnvironment): EditingContext[] {
  const contexts: EditingContext[] = [];

  for (const entry of env.flat) {
    const gridElement = entry.element;
    if (!GRID_ROLES.has(gridElement.role)) continue;

    const grid = detectGrid(gridElement);
    if (!grid || grid.rows.length === 0) continue;

    const surfaceLabel = gridSurfaceLabel(gridElement, entry.parent);
    const scopeRoots = inlineGridScopeRoots(entry, env);
    const blockers = collectBlockers(scopeRoots);
    const submitCandidates = collectSubmitCandidates(scopeRoots, blockers, 'label-match')
      .filter((submit) => submit.inference !== 'native-submit')
      .map((submit) => ({ ...submit, inference: 'toolbar-label' as const }));
    const submitDisabled = submitCandidates.some((submit) => submit.disabled);
    const rows = pickEditableRows(grid, gridElement, env, submitCandidates, blockers);
    if (rows.length === 0) continue;

    for (const row of rows) {
      const rowLabel = row.label || `row ${row.index + 1}`;
      const fields = gridRowFields({
        row,
        grid,
        env,
        surfaceLabel,
        blockers,
        submitDisabled,
      });
      if (fields.length === 0) continue;

      const confidence = gridContextConfidence(row, grid, submitCandidates, blockers);
      if (confidence === 'low') continue;

      contexts.push({
        id: buildContextIdentity('inline-grid-edit', surfaceLabel, rowLabel, row.index),
        kind: 'inline-grid-edit',
        flavor: gridElement.role === 'table' ? 'inline-table-edit' : 'inline-grid-edit',
        confidence,
        surfaceRef: grid.ref,
        surfaceLabel,
        recordRef: row.ref,
        recordLabel: rowLabel,
        recordIndex: row.index,
        fields,
        submitCandidates,
        blockers,
      });
    }
  }

  return contexts;
}

function collectFormFields(
  root: SnapshotElement,
  blockers: string[],
  submitDisabled: boolean
): EditingField[] {
  const fields: EditingField[] = [];

  const walk = (element: SnapshotElement): void => {
    if (FIELD_ROLES.has(element.role) && element.ref) {
      const label = elementLabel(element) || element.role;
      const rawValue = formElementValue(element);
      const value = isBlankValue(rawValue) ? '' : rawValue;
      const requiredness = fieldRequiredness({
        label,
        value,
        element,
        blockers,
        submitDisabled,
      });
      fields.push({
        identity: '',
        label,
        ref: element.ref,
        role: element.role,
        value,
        states: fieldStates({
          value,
          element,
          invalid: requiredness === 'confirmed' && value === '',
        }),
        requiredness,
        interaction: interactionForRole(element.role, element, false),
      });
    }

    for (const child of element.children) walk(child);
  };

  walk(root);
  return fields.filter((field) => !SKIP_FIELD_LABEL_RE.test(field.label));
}

interface GridRowFieldArgs {
  row: GridRow;
  grid: GridInfo;
  env: ExtractionEnvironment;
  surfaceLabel: string;
  blockers: string[];
  submitDisabled: boolean;
}

function gridRowFields(args: GridRowFieldArgs): EditingField[] {
  const { row, env, surfaceLabel, blockers, submitDisabled } = args;
  const fields: EditingField[] = [];

  for (const cell of row.cells) {
    const label = cleanLabel(cell.column || cell.gridcellLabel || '');
    if (!label || shouldSkipGridField(label, cell)) continue;

    const element = env.byRef.get(cell.ref);
    const { value, blank } = normalizedCellValue(cell);
    const invalid =
      fieldHasBlocker(label, blockers) || element?.attributes['aria-invalid'] === 'true';
    const readonly =
      Boolean(element && isReadonlyElement(element)) || /\bread-?only\b/i.test(cell.value);
    const requiredness = invalid ? 'confirmed' : blank && submitDisabled ? 'candidate' : 'unknown';
    const states = fieldStates({
      value,
      element,
      invalid,
      readonly,
      forceBlank: blank,
    });

    fields.push({
      identity: buildFieldIdentity('inline-grid-edit', surfaceLabel, row.label, row.index, label),
      label,
      ref: cell.ref,
      role: cell.role,
      value,
      states,
      requiredness,
      interaction: readonly ? 'readonly' : interactionForRole(cell.role, element, true),
    });
  }

  return fields;
}

function inlineGridScopeRoots(entry: FlatElement, env: ExtractionEnvironment): SnapshotElement[] {
  const ancestor = closestInlineGridScopeAncestor(entry.element, env);
  if (ancestor) return [ancestor];

  if (!entry.parent) return [entry.element];

  const start = Math.max(0, entry.index - INLINE_GRID_NEARBY_SIBLING_WINDOW);
  const end = Math.min(
    entry.parent.children.length,
    entry.index + INLINE_GRID_NEARBY_SIBLING_WINDOW + 1
  );
  return entry.parent.children.slice(start, end);
}

function closestInlineGridScopeAncestor(
  element: SnapshotElement,
  env: ExtractionEnvironment
): SnapshotElement | null {
  let current = env.parentByElement.get(element) ?? null;
  while (current) {
    if (INLINE_GRID_SCOPE_ROLES.has(current.role)) return current;
    current = env.parentByElement.get(current) ?? null;
  }
  return null;
}

function pickEditableRows(
  grid: GridInfo,
  gridElement: SnapshotElement,
  env: ExtractionEnvironment,
  submitCandidates: EditingSubmitCandidate[],
  blockers: string[]
): GridRow[] {
  const candidates = grid.rows.filter((row) => row.cells.length > 0);
  const explicit = candidates.filter(
    (row) =>
      isDraftLikeRow(row) ||
      row.isSelected ||
      row.cells.some((cell) => {
        const element = env.byRef.get(cell.ref);
        return Boolean(element && (isActiveElement(element) || FIELD_ROLES.has(element.role)));
      })
  );

  if (explicit.length > 0) return explicit.slice(0, 2);

  const hasEditSignals =
    grid.containsActive ||
    subtreeContainsActive(gridElement) ||
    (submitCandidates.some((submit) => submit.disabled) && blockers.length > 0);
  if (!hasEditSignals) return [];

  return candidates
    .filter((row) => row.cells.some((cell) => normalizedCellValue(cell).blank))
    .slice(0, 1);
}

function gridContextConfidence(
  row: GridRow,
  grid: GridInfo,
  submitCandidates: EditingSubmitCandidate[],
  blockers: string[]
): EditingContextConfidence {
  if (isDraftLikeRow(row) && (submitCandidates.length > 0 || blockers.length > 0)) return 'high';
  if (isDraftLikeRow(row) || blockers.length > 0) return 'medium';
  if (grid.containsActive && submitCandidates.length > 0) return 'medium';
  return 'low';
}

function collectSubmitCandidates(
  roots: SnapshotElement[],
  blockers: string[],
  defaultInference: EditingSubmitCandidate['inference']
): EditingSubmitCandidate[] {
  const candidates: EditingSubmitCandidate[] = [];

  const walk = (element: SnapshotElement): void => {
    const label = elementLabel(element);
    const isButtonLike = element.role === 'button' || element.role === 'menuitem';
    const isNativeSubmit =
      isButtonLike &&
      (element.attributes['type'] === 'submit' || element.attributes['formaction'] !== undefined);
    const labelMatches = Boolean(label && SUBMIT_LABEL_RE.test(label));

    if (element.ref && isButtonLike && (isNativeSubmit || labelMatches)) {
      const inference = isNativeSubmit ? 'native-submit' : defaultInference;
      const disabled = isDisabledElement(element);
      candidates.push({
        ref: element.ref,
        label: label || element.role,
        disabled,
        inference,
        blockers: disabled ? blockers : [],
      });
    }

    for (const child of element.children) walk(child);
  };

  for (const root of roots) walk(root);

  return dedupeBy(candidates, (candidate) => `${candidate.ref}:${candidate.label}`);
}

function collectBlockers(roots: SnapshotElement[]): string[] {
  const blockers: string[] = [];

  const walk = (element: SnapshotElement): void => {
    const text = cleanLabel(element.text || element.inputValue || '');
    if (text && isValidationBlockerText(text, element)) {
      blockers.push(text);
    }
    if (element.attributes['aria-invalid'] === 'true') {
      blockers.push(`${elementLabel(element) || element.role} is marked invalid`);
    }

    for (const child of element.children) walk(child);
  };

  for (const root of roots) walk(root);

  return dedupeStrings(blockers).slice(0, 8);
}

function isValidationBlockerText(text: string, element: SnapshotElement): boolean {
  if (text.length > 220 || !BLOCKER_TEXT_RE.test(text)) return false;
  if (GENERIC_REQUIRED_COPY_RE.test(text)) return false;
  return isLiveFeedbackElement(element) || SPECIFIC_BLOCKER_TEXT_RE.test(text);
}

function isLiveFeedbackElement(element: SnapshotElement): boolean {
  if (LIVE_FEEDBACK_ROLES.has(element.role)) return true;
  const ariaLive = element.attributes['aria-live'];
  return Boolean(ariaLive && ariaLive !== 'off');
}

function elementLabel(element: SnapshotElement): string {
  const display = getDisplayText(element);
  const raw =
    display ||
    element.text ||
    element.inputValue ||
    element.attributes['aria-label'] ||
    element.attributes['label'] ||
    element.attributes['title'] ||
    element.attributes['placeholder'] ||
    '';
  const label = cleanLabel(stripBlankPatterns(raw));
  return label === element.role ? '' : label;
}

function formElementValue(element: SnapshotElement): string {
  if (element.role === 'checkbox' || element.role === 'radio' || element.role === 'switch') {
    if (element.attributes['checked'] === 'true') return 'checked';
    return '';
  }

  return cleanLabel(element.inputValue || '');
}

function normalizedCellValue(cell: GridCell): { value: string; blank: boolean } {
  const raw = cleanLabel(stripBlankPatterns(cell.value));
  if (!raw || BLANK_CELL_RE.test(raw)) return { value: '', blank: true };

  const label = cleanLabel(cell.column || cell.gridcellLabel || '');
  if (label && raw.toLowerCase() === label.toLowerCase() && isInputLikeRole(cell.role)) {
    return { value: '', blank: true };
  }

  if (PLACEHOLDER_RE.test(raw) && raw.length <= 40 && !/\bselected\b/i.test(raw)) {
    return { value: '', blank: true };
  }

  return { value: raw, blank: false };
}

function fieldRequiredness(args: {
  label: string;
  value: string;
  element?: SnapshotElement;
  blockers: string[];
  submitDisabled: boolean;
}): EditingRequiredness {
  const { label, value, element, blockers, submitDisabled } = args;
  const requiredAttr =
    element?.attributes['required'] !== undefined ||
    element?.attributes['aria-required'] === 'true';
  const invalidAttr = element?.attributes['aria-invalid'] === 'true';
  if (requiredAttr || invalidAttr || fieldHasBlocker(label, blockers)) return 'confirmed';
  if (!value && submitDisabled) return 'candidate';
  return 'unknown';
}

function fieldStates(args: {
  value: string;
  element?: SnapshotElement;
  invalid?: boolean;
  readonly?: boolean;
  forceBlank?: boolean;
}): EditingFieldState[] {
  const states: EditingFieldState[] = [];
  if (args.forceBlank || isBlankValue(args.value)) {
    states.push('blank');
  } else {
    states.push('filled');
  }
  if (args.element && isActiveElement(args.element)) states.push('active');
  if (args.invalid) states.push('invalid');
  if (args.readonly || (args.element && isReadonlyElement(args.element))) states.push('readonly');
  return states;
}

function hasFieldState(field: EditingField, state: EditingFieldState): boolean {
  return field.states.includes(state);
}

function fieldHasBlocker(label: string, blockers: string[]): boolean {
  const normalizedLabel = normalizeForIdentity(label);
  if (!normalizedLabel) return false;
  const labelWords = normalizedLabel.split(' ').filter((word) => word.length > 2);

  return blockers.some((blocker) => {
    const normalizedBlocker = normalizeForIdentity(blocker);
    if (normalizedBlocker.includes(normalizedLabel)) return true;
    if (labelWords.length === 0) return false;
    return labelWords.every((word) => normalizedBlocker.includes(word));
  });
}

function shouldSkipGridField(label: string, cell: GridCell): boolean {
  if (SKIP_FIELD_LABEL_RE.test(label)) return true;
  if (READONLY_LABEL_RE.test(label) && !normalizedCellValue(cell).blank) return true;
  return false;
}

function interactionForRole(
  role: string | undefined,
  element: SnapshotElement | undefined,
  inGrid: boolean
): EditingInteraction {
  if (element && isReadonlyElement(element)) return 'readonly';
  if (role === 'combobox') return 'choose-option';
  if (role === 'checkbox' || role === 'radio' || role === 'switch') return 'toggle';
  if (role && FORM_INPUT_ROLES.has(role)) return 'type-input';
  return inGrid ? 'click-cell' : 'type-input';
}

function formatInteraction(interaction: EditingInteraction): string {
  switch (interaction) {
    case 'choose-option':
      return 'choose option';
    case 'click-cell':
      return 'click cell to edit';
    case 'type-input':
      return 'type input';
    case 'toggle':
      return 'toggle';
    case 'readonly':
      return 'read only';
  }
}

function isInputLikeRole(role: string | undefined): boolean {
  return Boolean(role && (FORM_INPUT_ROLES.has(role) || role === 'button'));
}

function isDraftLikeRow(row: GridRow): boolean {
  if (row.label && DRAFT_ROW_RE.test(row.label)) return true;
  if (row.cells.some((cell) => BLANK_CELL_RE.test(cell.value))) return true;
  return false;
}

function gridSurfaceLabel(gridElement: SnapshotElement, parent: SnapshotElement | null): string {
  const ownLabel = elementLabel(gridElement);
  if (ownLabel) return ownLabel;
  if (parent) {
    const parentLabel = elementLabel(parent);
    if (parentLabel && parentLabel !== parent.role) return `${parentLabel} grid`;
  }
  return 'grid';
}

function compareContextPriority(a: EditingContext, b: EditingContext): number {
  const confidenceScore = (confidence: EditingContextConfidence): number => {
    if (confidence === 'high') return 0;
    if (confidence === 'medium') return 1;
    return 2;
  };
  return confidenceScore(a.confidence) - confidenceScore(b.confidence);
}

function filterVisibleContexts(contexts: EditingContext[]): EditingContext[] {
  return contexts.filter(
    (context) => context.confidence === 'high' || context.confidence === 'medium'
  );
}

function describeContext(context: EditingContext): string {
  return context.kind === 'native-form' ? 'Native form edit' : 'Inline grid edit';
}

function matchEditingContexts(
  before: EditingContext[],
  after: EditingContext[]
): {
  matches: MatchedEditingContext[];
  unmatchedBefore: EditingContext[];
  unmatchedAfter: EditingContext[];
} {
  const matches: MatchedEditingContext[] = [];
  const unmatchedBefore = new Set(before);
  const unmatchedAfter = new Set(after);

  const addMatch = (beforeContext: EditingContext, afterContext: EditingContext): void => {
    matches.push({ before: beforeContext, after: afterContext });
    unmatchedBefore.delete(beforeContext);
    unmatchedAfter.delete(afterContext);
  };

  const beforeById = new Map(before.map((context) => [context.id, context]));
  for (const afterContext of after) {
    const beforeContext = beforeById.get(afterContext.id);
    if (beforeContext && unmatchedBefore.has(beforeContext)) {
      addMatch(beforeContext, afterContext);
    }
  }

  for (const afterContext of after) {
    if (!unmatchedAfter.has(afterContext)) continue;
    const candidates = [...unmatchedBefore].filter(
      (beforeContext) =>
        sameContextSurface(beforeContext, afterContext) &&
        beforeContext.recordIndex !== undefined &&
        beforeContext.recordIndex === afterContext.recordIndex &&
        fieldLabelOverlap(beforeContext, afterContext) > 0
    );
    if (candidates.length === 1) {
      addMatch(candidates[0], afterContext);
    }
  }

  for (const afterContext of after) {
    if (!unmatchedAfter.has(afterContext)) continue;
    const candidates = [...unmatchedBefore].filter((beforeContext) =>
      sameContextSurface(beforeContext, afterContext)
    );
    const afterInSurface = [...unmatchedAfter].filter((context) =>
      sameContextSurface(context, afterContext)
    );
    if (candidates.length !== 1 || afterInSurface.length !== 1) continue;

    const beforeContext = candidates[0];
    const overlap = fieldLabelOverlap(beforeContext, afterContext);
    const minOverlap = Math.min(2, beforeContext.fields.length, afterContext.fields.length);
    if (minOverlap > 0 && overlap >= minOverlap) {
      addMatch(beforeContext, afterContext);
    }
  }

  return {
    matches,
    unmatchedBefore: [...unmatchedBefore],
    unmatchedAfter: [...unmatchedAfter],
  };
}

function sameContextSurface(before: EditingContext, after: EditingContext): boolean {
  return (
    before.kind === after.kind &&
    normalizeForIdentity(before.surfaceLabel) === normalizeForIdentity(after.surfaceLabel)
  );
}

function fieldLabelOverlap(before: EditingContext, after: EditingContext): number {
  const beforeLabels = new Set(before.fields.map((field) => fieldCompareKey(field)));
  let overlap = 0;
  for (const field of after.fields) {
    if (beforeLabels.has(fieldCompareKey(field))) overlap++;
  }
  return overlap;
}

function fieldsByCompareKey(context: EditingContext): Map<string, EditingField> {
  const out = new Map<string, EditingField>();
  for (const field of context.fields) {
    out.set(fieldCompareKey(field), field);
  }
  return out;
}

function fieldCompareKey(field: EditingField): string {
  return normalizeForIdentity(field.label);
}

function compareSubmitState(
  matches: MatchedEditingContext[],
  changes: EditingContextChange[]
): void {
  for (const match of matches) {
    const beforeSubmits = submitsByCompareKey(match.before);
    const afterSubmits = submitsByCompareKey(match.after);

    for (const [identity, afterSubmit] of afterSubmits) {
      const beforeSubmit = beforeSubmits.get(identity);
      if (!beforeSubmit) continue;
      if (beforeSubmit.disabled === afterSubmit.disabled) {
        if (afterSubmit.disabled && match.after.blockers.length > 0) {
          changes.push({
            kind: 'save-blocked',
            contextId: match.after.id,
            surfaceLabel: match.after.surfaceLabel,
            recordLabel: match.after.recordLabel,
            ref: afterSubmit.ref,
            before: true,
            after: true,
            message: `${afterSubmit.label} stayed disabled; edit is still blocked.`,
          });
        }
        continue;
      }

      changes.push({
        kind: afterSubmit.disabled ? 'submit-disabled' : 'submit-enabled',
        contextId: match.after.id,
        surfaceLabel: match.after.surfaceLabel,
        recordLabel: match.after.recordLabel,
        ref: afterSubmit.ref,
        before: beforeSubmit.disabled,
        after: afterSubmit.disabled,
        message: `${afterSubmit.label} became ${afterSubmit.disabled ? 'disabled' : 'enabled'}.`,
      });
    }
  }
}

function submitsByCompareKey(context: EditingContext): Map<string, EditingSubmitCandidate> {
  const out = new Map<string, EditingSubmitCandidate>();
  for (const submit of context.submitCandidates) {
    out.set(normalizeForIdentity(submit.label), submit);
  }
  return out;
}

function compareBlockers(
  matches: MatchedEditingContext[],
  changes: EditingContextChange[]
): void {
  for (const match of matches) {
    const beforeBlockers = blockersByCompareKey(match.before);
    const afterBlockers = blockersByCompareKey(match.after);

    for (const [identity, afterBlocker] of afterBlockers) {
      if (beforeBlockers.has(identity)) continue;
      changes.push({
        kind: 'blocker-added',
        contextId: match.after.id,
        surfaceLabel: match.after.surfaceLabel,
        recordLabel: match.after.recordLabel,
        after: afterBlocker,
        message: `Validation blocker appeared: ${afterBlocker}`,
      });
    }

    for (const [identity, beforeBlocker] of beforeBlockers) {
      if (afterBlockers.has(identity)) continue;
      changes.push({
        kind: 'blocker-removed',
        contextId: match.after.id,
        surfaceLabel: match.after.surfaceLabel,
        recordLabel: match.after.recordLabel,
        before: beforeBlocker,
        message: `Validation blocker cleared: ${beforeBlocker}`,
      });
    }
  }
}

function blockersByCompareKey(context: EditingContext): Map<string, string> {
  const out = new Map<string, string>();
  for (const blocker of context.blockers) {
    out.set(normalizeForIdentity(blocker), blocker);
  }
  return out;
}

function buildContextIdentity(
  kind: EditingContextKind,
  surfaceLabel: string,
  recordLabel: string | undefined,
  recordIndex: number | undefined
): string {
  const surface = normalizeForIdentity(surfaceLabel) || 'unknown-surface';
  const record = normalizeForIdentity(recordLabel || '') || `row-${recordIndex ?? 0}`;
  return `${kind}:${surface}:${record}`;
}

function buildFieldIdentity(
  kind: EditingContextKind,
  surfaceLabel: string,
  recordLabel: string | undefined,
  recordIndex: number | undefined,
  fieldLabel: string
): string {
  return `${buildContextIdentity(kind, surfaceLabel, recordLabel, recordIndex)}:field:${normalizeForIdentity(fieldLabel)}`;
}

function normalizeForIdentity(value: string): string {
  return cleanLabel(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function cleanLabel(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function dedupeContexts(contexts: EditingContext[]): EditingContext[] {
  return dedupeBy(contexts, (context) => context.id);
}

function dedupeChanges(changes: EditingContextChange[]): EditingContextChange[] {
  return dedupeBy(
    changes,
    (change) =>
      `${change.kind}:${change.contextId ?? ''}:${change.fieldIdentity ?? ''}:${change.message}`
  );
}

function dedupeStrings(values: string[]): string[] {
  return dedupeBy(values, (value) => normalizeForIdentity(value));
}

function dedupeBy<T>(values: T[], keyFn: (value: T) => string): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const value of values) {
    const key = keyFn(value);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out;
}
